// frontend/src/api/network.ts
import api from './client';

const BASE = '/network';

export interface NetworkDevice {
  id: number;
  name: string;
  ip: string;
  description: string | null;
  default_wan: string;
  mac_address: string | null;
  organization: string | null;
  sort_order: number;
  is_online: boolean | null;
  latency_ms: number | null;
  bytes_in: number | null;
  bytes_out: number | null;
}

export interface WanProvider {
  id: number;
  wan_key: string;
  name: string;
  device_model: string | null;
  organization: string | null;
  phone: string | null;
  account_number: string | null;
  gateway_ip: string | null;
  notes: string | null;
}

export interface DhcpLease {
  id: string;
  ip: string;
  mac: string | null;
  hostname: string | null;
  status: string;
}

export interface NetworkSummary {
  wan1_up: boolean | null;
  wan2_up: boolean | null;
  wan1_latency_ms: number | null;
  wan2_latency_ms: number | null;
  last_poll: string | null;
  events: Array<{
    id: number;
    type: string;
    occurred_at: string;
    details: Record<string, unknown> | null;
  }>;
}

export interface RoutingRule {
  id: string | null;         // null = нет правила (WAN1 по умолчанию)
  name: string;
  comment: string;
  src_address: string;
  wan: string;               // "WAN1" | "WAN2" — текущий эффективный канал
  default_wan: string;       // WAN по умолчанию из справочника
  table: string | null;
  disabled: boolean | null;  // null = нет правила
  is_online: boolean | null;
  latency_ms: number | null;
}

export interface TrafficPoint {
  ts: string;
  bytes_in: number;
  bytes_out: number;
  latency_ms?: number | null;
  is_online?: boolean | null;
}

export interface WanPoint {
  ts: string;
  wan1_in: number;
  wan1_out: number;
  wan2_in: number;
  wan2_out: number;
  wan1_latency_ms?: number | null;
  wan2_latency_ms?: number | null;
  wan1_up?: boolean | null;
  wan2_up?: boolean | null;
}

export const networkApi = {
  getDevices: () => api.get<NetworkDevice[]>(`${BASE}/devices`).then(r => r.data),
  getSummary: () => api.get<NetworkSummary>(`${BASE}/summary`).then(r => r.data),
  getWanProviders: () => api.get<WanProvider[]>(`${BASE}/wan-providers`).then(r => r.data),
  getDhcpLeases: () => api.get<DhcpLease[]>(`${BASE}/dhcp-leases`).then(r => r.data),
  getDeviceHistory: (id: number, period: '24h' | '30d') =>
    api.get<TrafficPoint[]>(`${BASE}/devices/${id}/history`, { params: { period } }).then(r => r.data),
  getWan: (period: '24h' | '30d') =>
    api.get<WanPoint[]>(`${BASE}/wan`, { params: { period } }).then(r => r.data),
  getRouting: () => api.get<RoutingRule[]>(`${BASE}/routing`).then(r => r.data),
  patchRouting: (ruleId: string, disabled: boolean) =>
    api.patch(`${BASE}/routing/${encodeURIComponent(ruleId)}`, { disabled }).then(r => r.data),
  createRouting: (srcAddress: string) =>
    api.post<{ ok: boolean; id: string }>(`${BASE}/routing`, { src_address: srcAddress }).then(r => r.data),
};
